import React, { useMemo, useRef, useState } from 'react';
import {
  Box, Button, Divider, IconButton, Popover, TextField, Typography, InputAdornment,
  useTheme, alpha,
} from '@mui/material';
import {
  CalendarToday, ChevronLeft, ChevronRight, Clear,
} from '@mui/icons-material';
import {
  format, addMonths, subMonths, subDays,
  startOfMonth, endOfMonth, startOfWeek, startOfDay, startOfYear,
  addDays, isSameDay, isSameMonth, isBefore, isWithinInterval,
} from 'date-fns';

/**
 * DateRangePicker — elegant range picker inspired by the mock the user shared.
 *
 * Shape
 * -----
 * A field that looks like the other SearchableSelect-family filters (MUI
 * outlined TextField with floating label). Clicking opens a popover with:
 *   - Preset shortcuts on the left (Today, Last 7 days, This Month, …)
 *   - Dual-month calendar on the right with primary-tinted range highlight
 *   - Footer showing the pending selection + Clear / Cancel / Apply
 *
 * All draft changes stay local until Apply, so a user can preview presets or
 * a new selection and back out with Cancel.
 *
 * API
 * ---
 *   <DateRangePicker
 *     value={{ from: '2026-09-01', to: '2026-09-19' }}
 *     onChange={setRange}
 *     label="Date range"
 *   />
 *
 * `value.from` and `value.to` are ISO `yyyy-MM-dd` strings (or null). The
 * same shape is fed back through `onChange` so consumers can serialise to
 * URL params / API requests directly.
 */

export interface DateRange {
  from: string | null;
  to: string | null;
}

export interface DateRangePreset {
  key: string;
  label: string;
  /** Builder — called each open so relative windows like "Today" stay fresh. */
  make: () => DateRange;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  label?: string;
  placeholder?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  minWidth?: number | string;
  disabled?: boolean;
  /** Override the default preset list. Useful when a page needs its own
   *  window definitions (e.g. school-timezone-aware finance presets). */
  presets?: DateRangePreset[];
}

// ─── Date helpers ────────────────────────────────────────────────────────────

const DAY_LABELS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const iso = (d: Date) => format(d, 'yyyy-MM-dd');
const parseISO = (s: string | null | undefined): Date | null =>
  s ? new Date(`${s}T00:00:00`) : null;

// Default preset list. Rebuilt each open so "Today" stays correct as the
// clock rolls over during a long-lived session. Overridable via the
// component's `presets` prop when a page needs different windows.
const buildDefaultPresets = (): DateRangePreset[] => {
  const today = startOfDay(new Date());
  return [
    { key: 'today',      label: 'Today',        make: () => ({ from: iso(today), to: iso(today) }) },
    { key: 'yesterday',  label: 'Yesterday',    make: () => { const y = subDays(today, 1); return { from: iso(y), to: iso(y) }; } },
    { key: 'last7',      label: 'Last 7 Days',  make: () => ({ from: iso(subDays(today, 6)),  to: iso(today) }) },
    { key: 'last30',     label: 'Last 30 Days', make: () => ({ from: iso(subDays(today, 29)), to: iso(today) }) },
    { key: 'thisMonth',  label: 'This Month',   make: () => ({ from: iso(startOfMonth(today)), to: iso(today) }) },
    { key: 'lastMonth',  label: 'Last Month',   make: () => { const lm = subMonths(today, 1); return { from: iso(startOfMonth(lm)), to: iso(endOfMonth(lm)) }; } },
    { key: 'thisYear',   label: 'This Year',    make: () => ({ from: iso(startOfYear(today)), to: iso(today) }) },
  ];
};

const formatRangeText = (r: DateRange): string => {
  const f = parseISO(r.from);
  const t = parseISO(r.to);
  if (f && t) {
    if (isSameDay(f, t)) return format(f, 'MMM d, yyyy');
    return `${format(f, 'MMM d, yyyy')} — ${format(t, 'MMM d, yyyy')}`;
  }
  if (f) return `From ${format(f, 'MMM d, yyyy')}`;
  if (t) return `Until ${format(t, 'MMM d, yyyy')}`;
  return '';
};

// ─── Component ───────────────────────────────────────────────────────────────

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  label,
  placeholder = 'Any date',
  size = 'small',
  fullWidth = false,
  minWidth = 260,
  disabled,
  presets: presetsProp,
}) => {
  const theme = useTheme();
  const anchorRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange>(value);
  // `pickingEnd` is true after the first click of a fresh custom selection.
  // The second click completes the range (auto-swap if before the anchor).
  const [pickingEnd, setPickingEnd] = useState(false);
  const [hover, setHover] = useState<Date | null>(null);
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(parseISO(value.from) || new Date()));

  // Rebuild presets each open so relative windows ("Today", "Last 30 days")
  // stay accurate. When the consumer passes their own presets we still rebuild
  // per-open — they typically also use relative windows.
  const presets = useMemo(() => presetsProp ?? buildDefaultPresets(), [open, presetsProp]);

  // Which preset (if any) matches the current draft exactly?
  const activePresetKey = useMemo(() => {
    if (!draft.from || !draft.to) return null;
    const match = presets.find(p => {
      const r = p.make();
      return r.from === draft.from && r.to === draft.to;
    });
    return match?.key ?? null;
  }, [draft, presets]);

  // ─── Popover lifecycle ────────────────────────────────────────────────────

  const openPopover = () => {
    if (disabled) return;
    setDraft(value);
    setPickingEnd(false);
    setHover(null);
    // Anchor the left calendar to the currently-picked start (or today).
    setCursor(startOfMonth(parseISO(value.from) || new Date()));
    setOpen(true);
  };
  const closePopover = () => setOpen(false);
  const apply = () => {
    onChange(draft);
    closePopover();
  };
  const clearDraft = () => {
    setDraft({ from: null, to: null });
    setPickingEnd(false);
    setHover(null);
  };

  // ─── Selection handlers ───────────────────────────────────────────────────

  const setPreset = (p: DateRangePreset) => {
    const r = p.make();
    setDraft(r);
    setPickingEnd(false);
    setHover(null);
    if (r.from) setCursor(startOfMonth(parseISO(r.from)!));
  };

  const onDayClick = (day: Date) => {
    if (!pickingEnd) {
      setDraft({ from: iso(day), to: null });
      setPickingEnd(true);
      setHover(null);
      return;
    }
    // Second click completes the range. Auto-swap if user picked earlier
    // than the anchor — this matches Gmail / Airbnb / most range pickers.
    const anchor = parseISO(draft.from)!;
    if (isBefore(day, anchor)) {
      setDraft({ from: iso(day), to: iso(anchor) });
    } else {
      setDraft({ from: draft.from, to: iso(day) });
    }
    setPickingEnd(false);
    setHover(null);
  };

  // ─── Range-preview logic ──────────────────────────────────────────────────

  const draftStart = parseISO(draft.from);
  const draftEnd = parseISO(draft.to);
  // While picking end, the hovered cell drives a live preview of the range.
  const previewStart = pickingEnd && hover && draftStart && isBefore(hover, draftStart)
    ? hover : draftStart;
  const previewEnd = pickingEnd && hover && draftStart
    ? (isBefore(hover, draftStart) ? draftStart : hover)
    : draftEnd;

  const isInRange = (d: Date) => {
    if (!previewStart || !previewEnd) return false;
    if (isBefore(previewEnd, previewStart)) return false;
    return isWithinInterval(d, { start: startOfDay(previewStart), end: startOfDay(previewEnd) });
  };
  const isEndpointStart = (d: Date) => !!previewStart && isSameDay(d, previewStart);
  const isEndpointEnd   = (d: Date) => !!previewEnd   && isSameDay(d, previewEnd);

  // ─── Calendar grid builder ────────────────────────────────────────────────

  const buildMonthGrid = (monthStart: Date): Date[] => {
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  };

  const leftMonth = cursor;
  const rightMonth = addMonths(cursor, 1);

  // ─── Render helpers ───────────────────────────────────────────────────────

  const displayText = value.from || value.to ? formatRangeText(value) : '';
  const hasValue = !!(value.from || value.to);

  const renderDay = (day: Date, monthAnchor: Date) => {
    const inMonth = isSameMonth(day, monthAnchor);
    const inRange = isInRange(day);
    const start = isEndpointStart(day);
    const end = isEndpointEnd(day);
    const singleDay = start && end;
    const isToday = isSameDay(day, new Date());
    const isEndpoint = start || end;

    // Range fill (soft primary tint) is painted as a full-width bar with
    // rounded corners on the endpoints only — gives the connected-pill look.
    const barLeftRadius = start || singleDay;
    const barRightRadius = end || singleDay;

    return (
      <Box
        key={day.toISOString()}
        onClick={() => inMonth && onDayClick(day)}
        onMouseEnter={() => pickingEnd && inMonth && setHover(day)}
        sx={{
          position: 'relative',
          height: 34,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: inMonth ? 'pointer' : 'default',
        }}
      >
        {/* Range bar behind the number */}
        {(inRange || isEndpoint) && (
          <Box sx={{
            position: 'absolute',
            top: 3,
            bottom: 3,
            left: barLeftRadius ? 3 : 0,
            right: barRightRadius ? 3 : 0,
            bgcolor: isEndpoint
              ? theme.palette.primary.main
              : alpha(theme.palette.primary.main, 0.12),
            borderTopLeftRadius: barLeftRadius ? 8 : 0,
            borderBottomLeftRadius: barLeftRadius ? 8 : 0,
            borderTopRightRadius: barRightRadius ? 8 : 0,
            borderBottomRightRadius: barRightRadius ? 8 : 0,
          }} />
        )}
        {/* Today ring (only when not an endpoint) */}
        {isToday && !isEndpoint && (
          <Box sx={{
            position: 'absolute',
            top: 3, bottom: 3, left: 3, right: 3,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
            borderRadius: 1,
          }} />
        )}
        <Typography
          component="span"
          sx={{
            position: 'relative',
            zIndex: 1,
            fontSize: '0.8rem',
            fontWeight: isEndpoint ? 700 : (isToday ? 700 : 500),
            color: !inMonth
              ? 'text.disabled'
              : (isEndpoint ? '#fff' : 'text.primary'),
          }}
        >
          {day.getDate()}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <Box ref={anchorRef} sx={{ minWidth, width: fullWidth ? '100%' : undefined }}>
        <TextField
          fullWidth={fullWidth || undefined}
          size={size}
          label={label}
          placeholder={placeholder}
          value={displayText}
          onClick={openPopover}
          disabled={disabled}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: hasValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({ from: null, to: null });
                  }}
                  aria-label="Clear date range"
                  sx={{ p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : undefined,
            sx: { cursor: disabled ? 'default' : 'pointer' },
          }}
          inputProps={{ readOnly: true, style: { cursor: disabled ? 'default' : 'pointer' } }}
        />
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={closePopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 12px 32px rgba(0, 0, 0, 0.48)'
                : '0 12px 32px rgba(15, 23, 42, 0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', minWidth: 640 }}>
          {/* Presets sidebar */}
          <Box
            sx={{
              width: 148,
              py: 1,
              borderRight: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {presets.map(p => {
              const active = activePresetKey === p.key;
              return (
                <Box
                  key={p.key}
                  onClick={() => setPreset(p)}
                  sx={{
                    px: 2, py: 1,
                    mx: 0.75,
                    borderRadius: 1,
                    fontSize: '0.83rem',
                    fontWeight: active ? 700 : 500,
                    color: active ? theme.palette.primary.contrastText : 'text.primary',
                    bgcolor: active ? theme.palette.primary.main : 'transparent',
                    cursor: 'pointer',
                    transition: 'background-color 100ms ease',
                    '&:hover': {
                      bgcolor: active
                        ? theme.palette.primary.main
                        : alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  {p.label}
                </Box>
              );
            })}
            <Box sx={{ flex: 1 }} />
            {/* Custom-range hint — active when draft is set but no preset matches */}
            <Divider sx={{ mx: 1, my: 0.5 }} />
            <Box
              sx={{
                px: 2, py: 1, mx: 0.75, borderRadius: 1,
                fontSize: '0.83rem',
                fontWeight: !activePresetKey && (draft.from || draft.to) ? 700 : 500,
                color: !activePresetKey && (draft.from || draft.to)
                  ? theme.palette.primary.contrastText
                  : 'text.secondary',
                bgcolor: !activePresetKey && (draft.from || draft.to)
                  ? theme.palette.primary.main
                  : 'transparent',
              }}
            >
              Custom Range
            </Box>
          </Box>

          {/* Calendars */}
          <Box sx={{ p: 2, flex: 1 }}>
            {/* Month navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <IconButton size="small" onClick={() => setCursor(subMonths(cursor, 1))}>
                <ChevronLeft />
              </IconButton>
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'space-around' }}>
                <Typography variant="body2" fontWeight={700}>
                  {format(leftMonth, 'MMMM yyyy')}
                </Typography>
                <Typography variant="body2" fontWeight={700}>
                  {format(rightMonth, 'MMMM yyyy')}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setCursor(addMonths(cursor, 1))}>
                <ChevronRight />
              </IconButton>
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              {[leftMonth, rightMonth].map((monthAnchor, mi) => {
                const days = buildMonthGrid(monthAnchor);
                return (
                  <Box key={mi} sx={{ width: 224 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
                      {DAY_LABELS.map(d => (
                        <Box
                          key={d}
                          sx={{
                            textAlign: 'center',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            color: 'text.secondary',
                            letterSpacing: 0.3,
                            textTransform: 'uppercase',
                            py: 0.5,
                          }}
                        >
                          {d}
                        </Box>
                      ))}
                    </Box>
                    <Box
                      sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}
                      onMouseLeave={() => pickingEnd && setHover(null)}
                    >
                      {days.map(day => renderDay(day, monthAnchor))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Divider />
        <Box
          sx={{
            display: 'flex', alignItems: 'center',
            px: 2, py: 1.5, gap: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Box sx={{ flex: 1, fontSize: '0.83rem', color: 'text.secondary' }}>
            {draft.from && draft.to
              ? formatRangeText(draft)
              : (draft.from
                ? `From ${format(parseISO(draft.from)!, 'MMM d, yyyy')} · pick end date`
                : 'Pick a start date')}
          </Box>
          <Button
            size="small"
            onClick={clearDraft}
            disabled={!draft.from && !draft.to}
            sx={{ color: 'text.secondary', textTransform: 'none' }}
            startIcon={<Clear sx={{ fontSize: 16 }} />}
          >
            Clear
          </Button>
          <Button
            size="small"
            onClick={closePopover}
            sx={{ textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={apply}
            disabled={!draft.from || !draft.to}
            sx={{ textTransform: 'none', fontWeight: 700, minWidth: 84 }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;
